import React from "react";

const skeletonStyles: React.CSSProperties = {
  background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
  backgroundSize: "200px 100%",
  animation: "shimmer 1.5s infinite",
  display: "inline-block",
};

// CSS 애니메이션을 위한 스타일 태그
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: calc(200px + 100%) 0;
    }
  }
`;
document.head.appendChild(styleSheet);

interface SkeletonProps {
  width?: string;
  height?: string;
  borderRadius?: string;
  margin?: string;
  padding?: string;
  variant?: "text" | "rectangular" | "circular";
  className?: string;
  children?: React.ReactNode;
}

const Skeleton: React.FC<SkeletonProps> = ({
  width = "100%",
  height = "1em",
  borderRadius,
  margin = "0",
  padding = "0",
  variant = "text",
  className,
  children,
}) => {
  const getBorderRadius = () => {
    if (borderRadius) return borderRadius;
    if (variant === "circular") return "50%";
    if (variant === "text") return "4px";
    return "4px";
  };

  const style: React.CSSProperties = {
    ...skeletonStyles,
    width,
    height,
    borderRadius: getBorderRadius(),
    margin,
    padding,
  };

  return (
    <div style={style} className={className}>
      {children}
    </div>
  );
};

// 미리 정의된 스켈레톤 컴포넌트들
export const SkeletonText: React.FC<{
  lines?: number;
  width?: string;
  height?: string;
  spacing?: string;
}> = ({ lines = 1, width = "100%", height = "16px", spacing = "8px" }) => {
  return (
    <div>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          width={index === lines - 1 ? "75%" : width}
          height={height}
          margin={index > 0 ? `${spacing} 0 0 0` : "0"}
          variant="text"
        />
      ))}
    </div>
  );
};

export const SkeletonCard: React.FC<{
  width?: string;
  height?: string;
  showAvatar?: boolean;
  lines?: number;
}> = ({ width = "100%", height = "120px", showAvatar = false, lines = 3 }) => {
  return (
    <div style={{ width, height, padding: "16px" }}>
      {showAvatar && <Skeleton width="40px" height="40px" variant="circular" margin="0 0 12px 0" />}
      <SkeletonText lines={lines} />
    </div>
  );
};

export const SkeletonButton: React.FC<{
  width?: string;
  height?: string;
}> = ({ width = "120px", height = "40px" }) => {
  return <Skeleton width={width} height={height} borderRadius="8px" variant="rectangular" />;
};

export const SkeletonTable: React.FC<{
  rows?: number;
  columns?: number;
  width?: string;
}> = ({ rows = 5, columns = 4, width = "100%" }) => {
  return (
    <div style={{ width }}>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div
          key={rowIndex}
          style={{
            display: "flex",
            gap: "12px",
            marginBottom: "8px",
            alignItems: "center",
          }}
        >
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={colIndex} width={colIndex === 0 ? "60px" : "100%"} height="20px" variant="text" />
          ))}
        </div>
      ))}
    </div>
  );
};

export default Skeleton;
